import { collection, doc, onSnapshot, orderBy, query, updateDoc, where, type QueryConstraint, type Unsubscribe } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions, firestoreDb } from './firebase'
import type { FormDefinition, FormRequest, FormRequestSourceItem } from '../types/forms'

export type FormRequestMember = { id: string; uid: string; name: string; email: string; role: string }

export const DEBUG_MEMBER_FORM_DEFINITIONS: FormDefinition[] = [
  {
    id: 'debug-form-daily-sync',
    workspaceId: 'debug-team-workspace',
    title: 'Daily Sync Check-in',
    titleSearch: 'daily sync check-in',
    titlePrefixes: ['d', 'da', 'dai', 'dail', 'daily'],
    description: 'Short field update for today’s workspace sync.',
    fields: [
      { id: 'summary', type: 'TEXT', label: 'What changed today?', required: true, order: 0, multiline: true, placeholder: 'Share the operational update.', validation: { minLength: 3 } },
      { id: 'blocked', type: 'CHECKBOX', label: 'Blocked?', checkboxLabel: 'I am blocked on this work', required: false, order: 1, validation: {} },
    ],
    logic: { version: 1, rules: [] },
    theme: { density: 'COMPACT' },
    lifecycleStatus: 'ACTIVE',
    submissionCount: 0,
    lastSubmissionResponseId: null,
    deadline: null,
    isLocked: false,
    createdBy: 'debug-user',
    createdAt: null,
    updatedAt: null,
  },
]

export type SendFormRequestInput = {
  workspaceId: string
  formId: string
  recipientId: string
  sourceItem?: FormRequestSourceItem | null
}

export const sendFormRequest = async (input: SendFormRequestInput) => {
  const callable = httpsCallable<SendFormRequestInput, { requestId: string }>(firebaseFunctions, 'sendFormRequest')
  return (await callable(input)).data
}

export const subscribeFormRequestMembers = (workspaceId: string, requesterId: string, onChange: (members: FormRequestMember[]) => void, onError?: (error: Error) => void): Unsubscribe => {
  return onSnapshot(
    query(collection(firestoreDb, 'workspace_members'), where('workspaceId', '==', workspaceId)),
    (snapshot) => {
      const seen = new Set<string>()
      const members = snapshot.docs.flatMap((entry) => {
        const data = entry.data()
        const uid = typeof data.uid === 'string' && data.uid ? data.uid : typeof data.userId === 'string' ? data.userId : ''
        if (!uid || data.status !== 'ACTIVE' || seen.has(uid)) return []
        seen.add(uid)
        const email = typeof data.email === 'string' ? data.email : typeof data.user_email === 'string' ? data.user_email : ''
        const displayPreferences = data.displayPreferences && typeof data.displayPreferences === 'object' ? data.displayPreferences as Record<string, unknown> : null
        const name = typeof displayPreferences?.computedName === 'string' && displayPreferences.computedName
          ? displayPreferences.computedName
          : typeof data.displayName === 'string' && data.displayName
            ? data.displayName
            : typeof data.name === 'string' && data.name
              ? data.name
              : typeof data.username === 'string' && data.username
                ? data.username
                : typeof data.phone === 'string' && data.phone
                  ? data.phone
                  : email || 'Workspace member'
        return [{ id: entry.id, uid, name, email, role: typeof data.role === 'string' ? data.role : 'VIEWER' }]
      }).sort((left, right) => left.name.localeCompare(right.name))
      onChange(members.filter((member) => member.uid !== requesterId))
    },
    (error) => onError?.(error),
  )
}

export const subscribePendingFormRequests = (
  workspaceId: string,
  formId: string,
  onChange: (requests: FormRequest[]) => void,
  onError?: (error: Error) => void,
  requestedBy?: string,
): Unsubscribe => {
  const constraints: QueryConstraint[] = [
    where('workspaceId', '==', workspaceId),
    where('formId', '==', formId),
    where('status', '==', 'PENDING'),
  ]
  if (requestedBy) {
    constraints.push(where('requestedBy', '==', requestedBy))
  }
  constraints.push(orderBy('requestedAt', 'desc'))

  return onSnapshot(
    query(collection(firestoreDb, 'form_requests'), ...constraints),
    (snapshot) => onChange(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as FormRequest)),
    (error) => onError?.(error),
  )
}

export const subscribeAssignedFormRequests = (workspaceId: string, recipientId: string, onChange: (requests: FormRequest[]) => void, onError?: (error: Error) => void): Unsubscribe => {
  return onSnapshot(
    query(collection(firestoreDb, 'form_requests'), where('workspaceId', '==', workspaceId), where('recipientId', '==', recipientId), orderBy('requestedAt', 'desc')),
    (snapshot) => onChange(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as FormRequest)),
    (error) => onError?.(error),
  )
}

export const subscribeSentFormRequests = (workspaceId: string, requestedBy: string, onChange: (requests: FormRequest[]) => void, onError?: (error: Error) => void): Unsubscribe => {
  return onSnapshot(
    query(collection(firestoreDb, 'form_requests'), where('workspaceId', '==', workspaceId), where('requestedBy', '==', requestedBy), orderBy('requestedAt', 'desc')),
    (snapshot) => onChange(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as FormRequest)),
    (error) => onError?.(error),
  )
}

export const cancelFormRequest = async (requestId: string): Promise<void> => {
  await updateDoc(doc(firestoreDb, 'form_requests', requestId), { status: 'CANCELLED' })
}
